import { useState, useCallback } from 'react';
import { useClientMode } from '@/lib/clientMode';
import { useAuth } from '@/lib/auth';
import { X } from 'lucide-react';
import StepFabrics from './StepFabrics';
import StepClient from './StepClient';
import StepTryOn from './StepTryOn';
import StepOrder from './StepOrder';
import StepPayment from './StepPayment';

const STEPS = [
  { key: 'fabrics', label: 'Choose Your Fabric' },
  { key: 'client', label: 'Your Details' },
  { key: 'tryon', label: 'See Your Look' },
  { key: 'order', label: 'Your Order' },
  { key: 'payment', label: 'Confirm & Pay' },
];

export default function ClientModeWizard() {
  const { toggleClientMode } = useClientMode();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState('right');

  // Wizard state
  const [wizardState, setWizardState] = useState({
    selectedFabric: null,
    selectedGarmentType: 'suit',
    client: null,
    clientPhoto: null,
    tryOnResult: null,
    tryOnImageUrl: null,
    inclusivePrice: 0,
    measurements: null,
    measurementsLater: false,
    customisation: {},
    requiredByDate: null,
    orderId: null,
  });

  const updateState = useCallback((updates) => {
    setWizardState(prev => ({ ...prev, ...updates }));
  }, []);

  const goToStep = useCallback((step) => {
    setDirection(step > currentStep ? 'right' : 'left');
    setCurrentStep(step);
  }, [currentStep]);

  const nextStep = useCallback(() => {
    if (currentStep < STEPS.length - 1) {
      setDirection('right');
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setDirection('left');
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const resetWizard = useCallback(() => {
    setWizardState({
      selectedFabric: null,
      selectedGarmentType: 'suit',
      client: null,
      clientPhoto: null,
      tryOnResult: null,
      tryOnImageUrl: null,
      inclusivePrice: 0,
      measurements: null,
      measurementsLater: false,
      customisation: {},
      requiredByDate: null,
      orderId: null,
    });
    setCurrentStep(0);
    setDirection('right');
  }, []);

  const stepProps = {
    wizardState,
    updateState,
    nextStep,
    prevStep,
    goToStep,
    resetWizard,
    toggleClientMode,
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: '#FAFAF8' }} data-testid="client-mode-wizard">
      {/* Header */}
      <header className="h-16 flex items-center justify-between px-8 bg-white border-b" style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--brand-navy)' }}>
            <span className="text-white text-xs font-bold tracking-wider">H&S</span>
          </div>
          <div>
            <p className="text-sm font-semibold tracking-wide" style={{ color: 'var(--brand-navy)', fontFamily: "'Playfair Display', serif" }}>
              Herringbone & Sui
            </p>
            <p className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--brand-gold)' }}>Bespoke Menswear</p>
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex items-center gap-0" data-testid="wizard-progress-bar">
          {STEPS.map((step, i) => (
            <div key={step.key} className="flex items-center">
              <button
                onClick={() => i < currentStep && goToStep(i)}
                disabled={i > currentStep}
                className="flex flex-col items-center gap-1 group"
                data-testid={`progress-step-${step.key}`}
              >
                <div
                  className="w-3 h-3 rounded-full transition-all duration-300"
                  style={{
                    backgroundColor: i === currentStep ? 'var(--brand-gold)' : i < currentStep ? 'var(--brand-navy)' : 'rgba(27,42,74,0.12)',
                    transform: i === currentStep ? 'scale(1.4)' : 'scale(1)',
                    boxShadow: i === currentStep ? '0 0 0 4px rgba(197,165,90,0.2)' : 'none',
                    cursor: i < currentStep ? 'pointer' : 'default',
                  }}
                />
                <span className="text-[9px] font-medium hidden md:block transition-colors"
                  style={{ color: i === currentStep ? 'var(--brand-gold)' : i < currentStep ? 'var(--brand-navy)' : 'rgba(27,42,74,0.3)' }}>
                  {step.label}
                </span>
              </button>
              {i < STEPS.length - 1 && (
                <div className="w-12 lg:w-20 h-px mx-1" style={{ backgroundColor: i < currentStep ? 'var(--brand-navy)' : 'rgba(27,42,74,0.08)' }} />
              )}
            </div>
          ))}
        </div>

        <button
          onClick={toggleClientMode}
          className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all hover:bg-gray-100"
          style={{ color: 'var(--ink-muted)' }}
          data-testid="exit-client-mode-btn"
        >
          <X className="w-4 h-4" />
          <span className="hidden sm:inline">Exit</span>
        </button>
      </header>

      {/* Step Content */}
      <div className="flex-1 overflow-hidden relative">
        <div
          className="absolute inset-0 transition-transform duration-400 ease-out"
          style={{ transform: 'translateX(0)' }}
        >
          <div className="h-full overflow-y-auto">
            {currentStep === 0 && <StepFabrics {...stepProps} />}
            {currentStep === 1 && <StepClient {...stepProps} />}
            {currentStep === 2 && <StepTryOn {...stepProps} />}
            {currentStep === 3 && <StepOrder {...stepProps} />}
            {currentStep === 4 && <StepPayment {...stepProps} />}
          </div>
        </div>
      </div>
    </div>
  );
}
